import React, { useMemo } from 'react';

const formatMoney = (v) => Number(v || 0).toFixed(2).replace('.', ',');

export default function DishCost({ dish, ingredients, costDetails = [], menuItems = [], recipeOptionMap = null }) {
    const ingredientMap = useMemo(() => {
        const m = new Map();
        (ingredients || []).forEach((ing) => m.set(ing.id, ing));
        return m;
    }, [ingredients]);

    const resolveLineItem = (line, ingredientId) => {
        const lineType = (line.type || '').toString();
        const fromLine = lineType === 'component'
            ? (line.itemObject || line.component || line.componentObject || null)
            : (line.itemObject || line.ingredient || line.ingredientObject || null);

        if (fromLine?.id || fromLine?.name) {
            return {
                ...fromLine,
                id: Number(fromLine.id ?? ingredientId ?? 0),
                name: fromLine.name || line.itemName || line.name || `Item ${ingredientId}`,
                unit: fromLine.unit?.name || fromLine.unit || line.unit || '',
            };
        }

        const fallbackName = line.itemName || line.name || line.component?.name || line.ingredient?.name || '';
        if (fallbackName) {
            return {
                id: ingredientId,
                name: fallbackName,
                unit: line.unit || '',
            };
        }

        if (lineType === 'component') {
            return (
                ingredientMap.get(ingredientId) ||
                (recipeOptionMap && recipeOptionMap.get(`component:${ingredientId}`)) ||
                (recipeOptionMap && recipeOptionMap.get(`ingredient:${ingredientId}`)) ||
                (recipeOptionMap && recipeOptionMap.get(`${ingredientId}`)) ||
                (menuItems || []).find((m) => Number(m.id) === ingredientId) ||
                { id: ingredientId, name: `Composant inconnu (ID: ${ingredientId})`, unit: '' }
            );
        }

        return (
            ingredientMap.get(ingredientId) ||
            (recipeOptionMap && recipeOptionMap.get(`ingredient:${ingredientId}`)) ||
            { id: ingredientId, name: `Ingrédient inconnu (ID: ${ingredientId})`, unit: '' }
        );
    };

    const details = useMemo(() => {
        if (!Array.isArray(costDetails) || costDetails.length === 0) {
            return [];
        }

        return costDetails.map((detail) => {
            const ingredientId = Number(detail.ingredientId ?? detail.ingredient_id ?? 0);
            const ingredient = resolveLineItem(detail, ingredientId);
            const quantity = Number(detail.quantity ?? 0);
            const cost = Number(detail.cost ?? 0);
            const unitCost = quantity > 0 ? cost / quantity : 0;
            const path = Array.isArray(detail.componentPath) ? detail.componentPath : [];
            return {
                ...detail,
                ingredientId,
                name: detail.name || ingredient.name || `Ingrédient ${ingredientId}`,
                unit: detail.unit || ingredient.unit || '',
                quantity,
                cost,
                unitCost,
                componentPath: path,
            };
        });
    }, [costDetails, resolveLineItem]);

    const tree = useMemo(() => {
        const root = { id: null, name: 'root', children: new Map(), items: [] };

        details.forEach((detail) => {
            let node = root;
            detail.componentPath.forEach((componentId) => {
                const key = String(componentId);
                if (!node.children.has(key)) {
                    const componentItem = resolveLineItem({ type: 'component' }, componentId);
                    node.children.set(key, {
                        id: componentId,
                        name: componentItem.name,
                        children: new Map(),
                        items: [],
                    });
                }
                node = node.children.get(key);
            });
            node.items.push(detail);
        });

        return root;
    }, [details, resolveLineItem]);

    const renderTree = (node, depth = 0) => {
        const nodes = [];
        node.children.forEach((child) => {
            nodes.push(
                <div key={`component-${child.id}-${depth}`} className="space-y-3">
                    <div className="rounded-2xl bg-slate-50 p-3 border border-slate-200" style={{ paddingLeft: depth * 16 }}>
                        <div className="font-semibold text-slate-800">Composant: {child.name}</div>
                        {child.items.length > 0 && (
                            <div className="mt-2 space-y-2">
                                {child.items.map((detail, idx) => (
                                    <div key={`item-${child.id}-${idx}`} className="rounded-xl bg-white p-3 border border-slate-200">
                                        <div className="flex justify-between gap-4">
                                            <div>
                                                <div className="font-medium text-slate-800">{detail.name}</div>
                                                <div className="text-xs text-slate-500">
                                                    {detail.quantity} {detail.unit || ''} × Ar {formatMoney(detail.unitCost)}
                                                </div>
                                                <div className="text-xs text-slate-500">Type: {detail.componentPath.length > 0 ? 'Composant' : 'Ingrédient direct'}</div>
                                            </div>
                                            <div className="font-semibold text-slate-900">Ar {formatMoney(detail.cost)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {renderTree(child, depth + 1)}
                    </div>
                </div>
            );
        });

        if (depth === 0 && node.items.length > 0) {
            nodes.unshift(
                <div key="root-items" className="space-y-2">
                    {node.items.map((detail, idx) => (
                        <div key={`direct-${idx}`} className="rounded-2xl bg-white p-3 border border-slate-200">
                            <div className="flex justify-between gap-4">
                                <div>
                                    <div className="font-medium text-slate-800">{detail.name}</div>
                                    <div className="text-xs text-slate-500">{detail.quantity} {detail.unit || ''} × Ar {formatMoney(detail.unitCost)}</div>
                                    <div className="text-xs text-slate-500">Type: Ingrédient direct</div>
                                </div>
                                <div className="font-semibold text-slate-900">Ar {formatMoney(detail.cost)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        return nodes;
    };

    const total = details.reduce((sum, detail) => sum + detail.cost, 0);

    const shouldRenderTree = details.length > 0;

    return (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-green-700">
            <h4 className="font-semibold mb-2">Coût de revient</h4>
            <div className="space-y-4">
                {shouldRenderTree ? (
                    <div className="space-y-4">
                        {renderTree(tree)}
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 flex justify-between font-semibold text-slate-900">
                            <span>Total</span>
                            <span>Ar {formatMoney(total)}</span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {(dish.recipe || []).map((line, idx) => {
                            const ingredientId = Number(line.ingredientId ?? line.ingredient_id ?? line.component_id ?? line.ingredient_id ?? 0);
                            const ing = resolveLineItem(line, ingredientId);
                            const unit = ing.unit?.name || ing.unit || '';
                            const qty = Number(line.qty ?? line.quantity ?? 0);
                            const unitCost = parseFloat(ing.last_price ?? ing.unit_price ?? ing.cost_per_unit ?? ing.purchasePrice ?? 0) || 0;
                            const lineCost = unitCost * qty;
                            return (
                                <div key={`legacy-${idx}`} className="flex justify-between">
                                    <div>
                                        <div className="font-medium text-slate-800">{ing.name || `Ingrédient ${ingredientId}`}</div>
                                        <div className="text-xs text-slate-500">{qty} {unit} × Ar {formatMoney(unitCost)}</div>
                                    </div>
                                    <div className="font-semibold text-slate-900">Ar {formatMoney(lineCost)}</div>
                                </div>
                            );
                        })}
                        <hr className="my-2" />
                        <div className="flex justify-between font-semibold">Total:<span>Ar {formatMoney(total)}</span></div>
                    </div>
                )}
            </div>
        </div>
    );
}
